import React, {useState, useEffect} from 'react';
import {useAuth} from '../contexts/AuthContext';
import {usePhase} from '../contexts/PhaseContext';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';

const AdminPhases = () => {
    const [phases, setPhases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        uploadingStartsAt: '',
        votingStartsAt: '',
        closedAt: '',
        autoTransition: true,
    });

    const {user, token} = useAuth();
    const {phase, fetchCurrentPhase} = usePhase();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user?.isAdmin) {
            navigate('/dashboard');
            return;
        }
        fetchPhases();
    }, [user, navigate]);

    const fetchPhases = async () => {
        try {
            const response = await axios.get('/api/phases', {
                headers: {Authorization: `Bearer ${token}`},
            });
            setPhases(response.data);
        } catch (error) {
            setError('Failed to fetch phases');
            console.error('Error fetching phases:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePhase = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: formData.name,
                description: formData.description || undefined,
                uploadingStartsAt: formData.uploadingStartsAt || undefined,
                votingStartsAt: formData.votingStartsAt || undefined,
                closedAt: formData.closedAt || undefined,
                autoTransition: formData.autoTransition,
            };

            await axios.post('/api/phases', payload, {
                headers: {Authorization: `Bearer ${token}`},
            });

            setFormData({
                name: '',
                description: '',
                uploadingStartsAt: '',
                votingStartsAt: '',
                closedAt: '',
                autoTransition: true,
            });
            setShowCreateForm(false);
            fetchPhases();
            fetchCurrentPhase(); // Update the global phase state
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to create phase');
        }
    };

    const handleManualPhaseChange = async (newPhase) => {
        if (!window.confirm(`Are you sure you want to manually set the phase to ${newPhase}?`)) {
            return;
        }

        try {
            await axios.post('/api/phases/set-phase', {phase: newPhase}, {headers: {Authorization: `Bearer ${token}`}});
            fetchPhases();
            fetchCurrentPhase(); // Update the global phase state
        } catch (error) {
            setError(error.response?.data?.error || 'Failed to set phase');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getLocalDateTimeString = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const offset = d.getTimezoneOffset();
        const localDate = new Date(d.getTime() - offset * 60 * 1000);
        return localDate.toISOString().slice(0, 16);
    };

    if (!user?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
                    <p className="mt-2 text-gray-600">Admin access required</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">Phase Management</h1>
                    <p className="mt-2 text-gray-600">Control the art swap cycle phases and scheduling</p>
                </div>

                {error && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path
                                        fillRule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                            <div className="ml-auto pl-3">
                                <button onClick={() => setError('')} className="inline-flex text-red-400 hover:text-red-600">
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path
                                            fillRule="evenodd"
                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Current Phase Status */}
                <div className="mb-6 bg-white shadow rounded-lg p-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Current Phase Status</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-2xl font-bold text-gray-900">{phase.currentPhase}</div>
                            <div className="text-sm text-gray-600">{phase.name}</div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-lg font-medium text-gray-900">{phase.autoTransition ? 'Auto' : 'Manual'}</div>
                            <div className="text-sm text-gray-600">Transition Mode</div>
                        </div>
                        <div className="text-center p-4 bg-gray-50 rounded-lg">
                            <div className="text-lg font-medium text-gray-900">{phase.nextTransition?.nextPhase || 'No scheduled transition'}</div>
                            <div className="text-sm text-gray-600">Next Phase</div>
                        </div>
                    </div>
                </div>

                {/* Manual Phase Controls */}
                <div className="mb-6 bg-white shadow rounded-lg p-6">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Manual Phase Control</h2>
                    <div className="flex space-x-4">
                        <button
                            onClick={() => handleManualPhaseChange('Uploading')}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                            Set to Uploading
                        </button>
                        <button
                            onClick={() => handleManualPhaseChange('Voting')}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                            Set to Voting
                        </button>
                        <button
                            onClick={() => handleManualPhaseChange('Closed')}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                        >
                            Set to Closed
                        </button>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">Manual phase changes will disable automatic transitions for the current phase.</p>
                </div>

                {/* Create New Phase */}
                <div className="mb-6">
                    <button
                        onClick={() => setShowCreateForm(!showCreateForm)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create Scheduled Phase
                    </button>
                </div>

                {/* Create Phase Form */}
                {showCreateForm && (
                    <div className="mb-6 bg-white shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Create New Scheduled Phase</h2>
                        <form onSubmit={handleCreatePhase} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                        Phase Name
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="e.g., June 2024 Art Swap"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="autoTransition" className="block text-sm font-medium text-gray-700">
                                        Auto Transition
                                    </label>
                                    <select
                                        id="autoTransition"
                                        value={formData.autoTransition}
                                        onChange={(e) => setFormData({...formData, autoTransition: e.target.value === 'true'})}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="true">Enabled</option>
                                        <option value="false">Disabled</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                    Description (Optional)
                                </label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows={2}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label htmlFor="uploadingStartsAt" className="block text-sm font-medium text-gray-700">
                                        Uploading Starts At
                                    </label>
                                    <input
                                        type="datetime-local"
                                        id="uploadingStartsAt"
                                        value={formData.uploadingStartsAt}
                                        onChange={(e) => setFormData({...formData, uploadingStartsAt: e.target.value})}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="votingStartsAt" className="block text-sm font-medium text-gray-700">
                                        Voting Starts At
                                    </label>
                                    <input
                                        type="datetime-local"
                                        id="votingStartsAt"
                                        value={formData.votingStartsAt}
                                        onChange={(e) => setFormData({...formData, votingStartsAt: e.target.value})}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="closedAt" className="block text-sm font-medium text-gray-700">
                                        Closes At
                                    </label>
                                    <input
                                        type="datetime-local"
                                        id="closedAt"
                                        value={formData.closedAt}
                                        onChange={(e) => setFormData({...formData, closedAt: e.target.value})}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    type="submit"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Create Phase
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateForm(false)}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Phase History */}
                <div className="bg-white shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Phase History</h2>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Current Phase
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {phases.map((phaseItem) => (
                                        <tr key={phaseItem.id}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{phaseItem.name}</div>
                                                {phaseItem.description && <div className="text-sm text-gray-500">{phaseItem.description}</div>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                        phaseItem.currentPhase === 'Uploading'
                                                            ? 'bg-green-100 text-green-800'
                                                            : phaseItem.currentPhase === 'Voting'
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}
                                                >
                                                    {phaseItem.currentPhase}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                <div>Upload: {formatDate(phaseItem.uploadingStartsAt)}</div>
                                                <div>Vote: {formatDate(phaseItem.votingStartsAt)}</div>
                                                <div>Close: {formatDate(phaseItem.closedAt)}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                        phaseItem.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                                    }`}
                                                >
                                                    {phaseItem.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                                {phaseItem.autoTransition && <div className="text-xs text-gray-500 mt-1">Auto-transition</div>}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(phaseItem.createdAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPhases;
